import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import Modal from '../ui/Modal.jsx';
import FormField from '../ui/FormField.jsx';
import Button from '../ui/Button.jsx';
import { useAuthStore } from '../../store/authStore.js';
import { toastError, toastSuccess } from '../../hooks/useToast.js';

const inputClass =
  'w-full rounded-xl border border-white/15 bg-slate-950/80 px-4 py-3 text-sm text-slate-100 outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/40';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AdminProfileModal({ open, onClose }) {
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      nombre: '',
      email: '',
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
    },
  });

  const newPassword = watch('new_password');
  const currentPassword = watch('current_password');
  const newPasswordConfirmation = watch('new_password_confirmation');
  const changingPassword =
    Boolean(newPassword?.trim()) ||
    Boolean(currentPassword?.trim()) ||
    Boolean(newPasswordConfirmation?.trim());

  useEffect(() => {
    if (!open || !user) return;
    reset({
      nombre: user.nombre ?? '',
      email: user.email ?? '',
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }, [open, user, reset]);

  async function onSubmit(values) {
    try {
      await updateProfile({
        nombre: values.nombre.trim(),
        email: values.email.trim(),
        current_password: values.current_password,
        new_password: values.new_password,
        new_password_confirmation: values.new_password_confirmation,
      });
      toastSuccess('Perfil actualizado', 'Tus datos de acceso se guardaron correctamente.');
      onClose();
    } catch (e) {
      const fields = e.payload?.details ?? e.payload?.errors;
      if (fields && typeof fields === 'object') {
        Object.entries(fields).forEach(([key, message]) => {
          if (typeof message === 'string') {
            setError(key, { type: 'server', message });
          }
        });
        toastError('Revisa el formulario', e.message);
        return;
      }
      toastError('No se pudo actualizar', e.message);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Mi cuenta">
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <p className="text-sm leading-relaxed text-slate-400">
          Actualiza tu nombre de usuario, correo de acceso y contraseña del panel administrativo.
        </p>

        <FormField label="Nombre de usuario" error={errors.nombre?.message} required>
          <input
            className={inputClass}
            autoComplete="name"
            {...register('nombre', {
              required: 'El nombre es obligatorio',
              minLength: { value: 2, message: 'Mínimo 2 caracteres' },
              maxLength: { value: 120, message: 'Máximo 120 caracteres' },
            })}
          />
        </FormField>

        <FormField label="Correo electrónico" error={errors.email?.message} required>
          <input
            type="email"
            className={inputClass}
            autoComplete="email"
            {...register('email', {
              required: 'El correo es obligatorio',
              pattern: { value: EMAIL_PATTERN, message: 'Ingresa un correo válido' },
            })}
          />
        </FormField>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="text-sm font-semibold text-white">Cambiar contraseña</p>
          <p className="mt-1 text-xs text-slate-400">
            Déjalo en blanco si no deseas cambiar la contraseña.
          </p>

          <div className="mt-4 space-y-4">
            <FormField label="Contraseña actual" error={errors.current_password?.message}>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  className={`${inputClass} pr-11`}
                  autoComplete="current-password"
                  {...register('current_password', {
                    validate: (value) =>
                      !changingPassword || value?.trim()
                        ? true
                        : 'Ingresa tu contraseña actual',
                  })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-white"
                  onClick={() => setShowCurrentPassword((v) => !v)}
                  aria-label={showCurrentPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Nueva contraseña" error={errors.new_password?.message}>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className={`${inputClass} pr-11`}
                  autoComplete="new-password"
                  {...register('new_password', {
                    validate: (value) => {
                      if (!changingPassword) return true;
                      if (!value?.trim()) return 'Ingresa la nueva contraseña';
                      if (value.length < 8) return 'Mínimo 8 caracteres';
                      return true;
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-white"
                  onClick={() => setShowNewPassword((v) => !v)}
                  aria-label={showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>

            <FormField label="Confirmar nueva contraseña" error={errors.new_password_confirmation?.message}>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`${inputClass} pr-11`}
                  autoComplete="new-password"
                  {...register('new_password_confirmation', {
                    validate: (value) => {
                      if (!changingPassword) return true;
                      if (!value?.trim()) return 'Confirma la nueva contraseña';
                      if (value !== newPassword) return 'Las contraseñas no coinciden';
                      return true;
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-white"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </FormField>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
